import React from "react";
import Image from "next/image";
import { useRouter } from "next/router";
import poster from "../../public/neet-pg/neet-pghero.webp";
import axios from "axios";
import Cookies from "js-cookie";

export default function HeroSection() {
  const router = useRouter();

  const [featuresArray, setFeaturesArray] = React.useState([]);
  const [priceBasic, setPriceBasic] = React.useState(null);

  React.useEffect(() => {
    async function fetchPricing() {
      try {
        const API_URL = process.env.NEXT_PUBLIC_API_URL || "";
        if (!API_URL) return;

        const res = await axios.get(`${API_URL}/api/fetch-pgservices-data`);

        if (res?.data?.data?.length > 0) {
          const fetched = res.data.data[0];

          // Get price
          setPriceBasic(
            typeof fetched?.priceDetails?.pgbasic !== "undefined"
              ? fetched.priceDetails.pgbasic
              : null
          );

          // Build features array for basic
          const sectionOrder = ["tools", "analysis", "postExamMentorship"];
          const allSections = Object.keys(fetched.features || {}).filter(
            (s) => s !== "lastUpdatedBy" && s !== "_id"
          );
          const orderedSections = [
            ...sectionOrder.filter((s) => allSections.includes(s)),
            ...allSections.filter((s) => !sectionOrder.includes(s)),
          ];

          const arr = [];
          orderedSections.forEach((section) => {
            const sectionFeatures = fetched.features[section] || {};
            Object.keys(sectionFeatures).forEach((featureKey) => {
              if (sectionFeatures?.[featureKey]?.["pgbasic"] === "Yes") {
                arr.push(
                  String(featureKey)
                    .replace(/([a-z])([A-Z])/g, "$1 $2")
                    .replace(/[_-]/g, " ")
                    .replace(/&/g, " & ")
                    .trim()
                );
              }
            });
          });

          setFeaturesArray(arr);
        }
      } catch (err) {
        // fallback removed: do not set static features or price
      }
    }

    fetchPricing();
  }, []);

  const handleGetStarted = (e) => {
    e.preventDefault();

    const tier = "pgbasic";
    const authToken = Cookies.get("authToken");
    const isLoggedIn =
      authToken &&
      authToken !== "undefined" &&
      authToken !== "null" &&
      authToken.trim() !== "";

    // Only proceed if priceBasic is set from backend
    if (priceBasic === null) {
      alert("Pricing information is not available. Please try again later.");
      return;
    }

    const checkoutQuery = `tier=${encodeURIComponent(
      tier
    )}&price=${encodeURIComponent(priceBasic)}&features=${encodeURIComponent(
      JSON.stringify(featuresArray)
    )}`;
    const checkoutPath = `/checkout?${checkoutQuery}`;

    if (!isLoggedIn) {
      try {
        sessionStorage.setItem(
          "checkoutAfterLogin",
          JSON.stringify({ tier, price: priceBasic, features: featuresArray })
        );
      } catch (e) { }

      const loginHost = "https://portal.neetsupport.com";
      const returnUrl = window.location.origin + checkoutPath;
      const loginUrl = `${loginHost}/login?next=${encodeURIComponent(returnUrl)}`;
      window.location.href = loginUrl;
      return;
    }

    router.push({
      pathname: "/checkout",
      query: {
        tier: tier,
        price: priceBasic,
        features: JSON.stringify(featuresArray),
      },
    });
  };

  // ✅ IMPORTANT: use the real public URL so opening in new tab does NOT download
  // poster.src becomes "/neet-pg/neet-pghero.webp"
  const posterSrc = poster?.src || "/neet-pg/neet-pghero.webp";

  return (
    <section className="w-full mx-auto px-4 sm:px-6 lg:px-8 py-12 relative">
      <div className="bg-[#fff2ed] rounded-2xl p-6 sm:p-10 lg:p-14 flex flex-col lg:flex-row items-center gap-8">
        {/* Left Content */}
        <div className="w-full lg:w-1/2">
          <h1 className="text-xl sm:text-3xl xl:text-4xl font-extrabold text-gray-900 leading-tight mb-4">
            Find the Best MD/MS Seat Matching{" "}
            <span className="text-[#ff9245]">Your Rank</span>
          </h1>

          <p className="text-base xl:text-lg text-[#6b5f7a] mb-6">
            Powerful tools and experienced mentors to guide your PG journey.
          </p>

          <ul className="mb-6 space-y-3">
            {[
              "AI-powered Cut-off & Allotment Predictor",
              "Detailed Fee, Bond, Stipend & Seat Matrix Across All States",
              "Compare Colleges Side-by-Side with Real Data",
              "Explore 2000+ Institutes with Verified Information",
              "Get Your Doubts Solved in a Personal 1:1 Session",
            ].map((item, index) => (
              <li
                key={index}
                className="flex items-start gap-2 text-gray-800 text-sm xl:text-lg"
              >
                <span className="text-[#ff9245] text-xl">✔</span>
                <span>{item}</span>
              </li>
            ))}
          </ul>

          {/* Discount Section */}
          <div className="mb-6 flex items-center gap-6 flex-wrap">
            <div className="flex flex-col gap-1">
              <div className="flex items-baseline gap-2">
                <span className="text-sm line-through text-gray-400">₹4999</span>
                <span className="text-2xl font-bold text-[#ff9245]">
                  ₹{priceBasic !== null ? priceBasic : "--"}
                </span>
              </div>
            </div>
          </div>

          {/* CTA Button */}
          <div className="mt-4 flex items-center justify-start gap-4">
            <button
              onClick={handleGetStarted}
              className="inline-flex items-center justify-between gap-1 bg-[#ff9245] text-white pl-6 pr-2 py-2 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 active:scale-95 text-xs"
              aria-label="Start Your Counselling Journey"
            >
              <span className="font-semibold text-lg">Start Your Journey</span>

              <span className="w-6 h-6 bg-white rounded-full flex items-center justify-center">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="w-3 h-3 text-[#ff9245]"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2.5}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M7 17L17 7M17 7H7M17 7v10"
                  />
                </svg>
              </span>
            </button>
          </div>
        </div>

        {/* Right Image */}
        <div className="w-full lg:w-1/2 flex justify-center items-center">
          <div className="relative w-full h-80 sm:h-96 md:h-[450px] lg:h-[500px]">
            <img
              src="/neet-pg/neet-pghero.webp"
              alt="neet pg counselling 2026"
              loading="eager"
              className="object-contain rounded-2xl w-full h-full select-none"
              draggable={false}
            />
          </div>
        </div>

      </div>
    </section>
  );
}
